import { doesUserHaveReleaseToggle } from '@skyspecs/release-utils-client';
import { getDefaultStore, useAtom } from 'jotai';
import { RESET, useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useMemo } from 'react';

import { useFeatures } from 'utils/features';

import { releaseTogglesAtom, releaseTogglesDisabledAtom } from './account/atoms';

export const NEW_IMAGE_VIEWER = 'new_image_viewer';
export const WORK_CONTAINER_DELETE_COUNTS = 'work_container_delete_counts';
export const COST_MANAGEMENT = 'cost_management';
export const WORK_CONTAINER_FILTER_IMPROVEMENT = 'work_container_custom_fields_filter_improvement';
export const SITE_DAMAGE_AND_INSPECTION_TABLES = 'site_damage_and_inspection_tables';
export const GLOBAL_SAVED_VIEWS = 'global_saved_views';
export const BULK_WORK_ORDER_GROUPING = 'bulk_work_order_grouping';
export const FLEET_TABLE_SAVED_VIEWS = 'fleet_table_saved_views';
export const RULES_CALCULATE_CRITICAL = 'rules_calculate_critical';
export const WORK_ORDER_TEMPLATES = 'work_order_templates';
export const ALLOW_PROCESS_RBTS = 'allow-process-rbts';
export const WO_DETAILS_SAVED_VIEWS = 'wo_details_saved_views';
export const IMPROVE_RATES_AND_ESTIMATES = 'improve_rates_and_estimates';
export const SIDEBAR_IMPROVEMENTS = 'sidebar_improvements_q3_2025';
export const ATTACHMENTS_IMPROVEMENTS = 'attachments_improvements_q3_2025';
export const IMAGE_VIEWER_LEVELS = 'image_viewer_levels';
export const WORK_LOG_TIMEZONES = 'work_log_timezones';
export const WO_IMPROVEMENTS = 'work_order_improvements_q3_2025';
export const REPLACE_DRAGULA = 'replace_dragula';
export const DARK_MODE = 'dark_mode';
export const INSPECTION_UPLOAD_IMPROVEMENTS = 'inspection_upload_improvements';
export const INSPECTION_UPLOAD_IMPROVEMENTS_REVERT = 'inspection_upload_improvements_revert';
export const RELEASE_NOTIFICATION = 'release_notification';
export const WORK_LOG_APPROVAL = 'work_log_approval';
export const INSPECTION_UPLOAD_SORT_FILTER_ADDITIONS = 'inspection_upload_sort_filter_additions';
export const IS_360_VIEWER = '360_viewer';
export const SKYCRAWLER_UPLOAD = 'skycrawler-upload';
export const AERO_ENTERPRISE_UPLOAD = 'aero-enterprise-upload';
export const BLADE_CARDS = 'blade_cards';
export const TASK_DETAIL_IMPROVEMENTS = 'task_detail_improvements_q1_2026';
export const COLOR_CODED_OBSERVATIONS = 'color_coded_observations';
export const WORK_ORDER_BULK_DELIVERY = 'work_order_bulk_delivery';
export const MEASUREMENT_TOOL = 'measurement_tool';
export const GRAYSCALE_IMAGE_FILTER = 'grayscale_image_filter';
export const INSPECTION_MAP_VIEW = 'inspection_map_view';
export const COLLAPSE_HEADER = 'collapse_header';
export const THUMBNAIL_REVIEWER = 'thumbnail_reviewer';
export const DAMAGE_TABLE_PICTURE_QUERY_UPDATE = 'damage_table_picture_query_update';
export const BLADE_VIEW = 'blade_view';
export const TASK_STEP_IMPROVEMENTS = 'task_step_improvements';
export const MULTI_TURBINE_BULK_UPLOADS = 'multi_turbine_bulk_uploads';
export const DAMAGE_CREATED_DATE_COLUMN = 'damage_created_date_column';
export const DAMAGES_DB_MAINTENANCE_BANNER = 'damages_db_maintenance_banner';
export const INSPECTION_CONSOLIDATED_FILTERS = 'inspection_consolidated_filters';
export const SCHEDULE_VIEWER = 'schedule_viewer';
export const DAMAGES_INBOX = 'damages_inbox';
export const BULK_UPDATE_PICTURES = 'bulk_update_pictures';
export const EXECUTIVE_DASHBOARD = 'executive_dashboard';

/**
 * When an internal user has the `INTERNAL_TESTER` feature flag, they are able
 * to enable and disable their use of release toggles. By default, release
 * toggles will be enabled. When disabled, the entry `user` will be added to
 * local storage and that value will be read when making GraphQL requests. When
 * set, the header `X-SkySpecs-IgnoreToggles` will be sent with `user`.
 */
export function useDisableReleaseToggles() {
  const { INTERNAL_TESTER } = useFeatures().features;
  const [disabledReleaseToggles, setDisabledReleaseToggles] = useAtom(releaseTogglesDisabledAtom);

  const releaseTogglesDisabled = useMemo(() => {
    return disabledReleaseToggles.includes('user');
  }, [disabledReleaseToggles]);

  const toggleDisabledReleaseToggles = useAtomCallback(
    useCallback((get, set) => {
      const existing = get(releaseTogglesDisabledAtom);
      const newValue = existing.includes('user')
        ? existing.filter(item => item !== 'user')
        : [...existing, 'user'];

      set(releaseTogglesDisabledAtom, newValue.length === 0 ? RESET : newValue);

      // add a slight delay so the action is not so jarring
      setTimeout(() => {
        // reload the window after making changes to make sure the app shows the
        // appropriate state
        window.location.reload();
      }, 500);
    }, [])
  );

  useEffect(() => {
    if (INTERNAL_TESTER) {
      if (disabledReleaseToggles.length === 0) {
        setDisabledReleaseToggles(RESET);
      } else {
        setDisabledReleaseToggles(disabledReleaseToggles);
      }
    } else {
      setDisabledReleaseToggles(RESET);
    }
  }, [disabledReleaseToggles, INTERNAL_TESTER, setDisabledReleaseToggles]);

  return {
    canManageReleaseToggles: INTERNAL_TESTER,
    releaseTogglesDisabled,
    toggleDisabledReleaseToggles,
  };
}

// Whenever possible get release toggles via hasReleaseToggle from useAccountContext().
// This function reads the jotai store directly and is not updated after the initial load.
const jotaiStore = getDefaultStore();
export function hasReleaseToggle(releaseToggleName: string) {
  const releaseToggles: string[] = jotaiStore.get(releaseTogglesAtom);
  return doesUserHaveReleaseToggle({ releaseToggles }, releaseToggleName);
}
